import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Scale, Sun, Moon, Languages, AlertCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { t, language, toggleLanguage, direction } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(language === 'ar' ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError(language === 'ar' ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Register the user
      await axios.post(`${API_URL}/api/auth/register`, {
        username: formData.username,
        password: formData.password,
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone
      });

      // Now login with the same credentials
      await login(formData.username, formData.password);
      
      toast.success(language === 'ar' ? 'تم إنشاء الحساب بنجاح' : 'Account created successfully');
      navigate('/portal');
    } catch (err) {
      let errorMsg = language === 'ar' ? 'فشل إنشاء الحساب' : 'Registration failed';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (detail.includes('Username already exists')) {
          errorMsg = language === 'ar' ? 'اسم المستخدم موجود مسبقاً' : 'Username already exists';
        } else if (detail.includes('Email already exists')) {
          errorMsg = language === 'ar' ? 'البريد الإلكتروني مسجل مسبقاً' : 'Email already registered';
        } else {
          errorMsg = detail;
        }
      }
      
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const BackArrow = direction === 'rtl' ? ArrowRight : ArrowLeft;

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-muted"
      dir={direction}
    >
      {/* Background */}
      <div 
        className="absolute inset-0 z-0 opacity-10"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1589829545856-d10d557cf95f?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />

      {/* Theme & Language Controls */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <Button variant="ghost" size="sm" onClick={toggleLanguage} className="gap-2">
          <Languages className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm">{language === 'en' ? 'العربية' : 'English'}</span>
        </Button>
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {isDark ? <Sun className="w-5 h-5" strokeWidth={1.5} /> : <Moon className="w-5 h-5" strokeWidth={1.5} />}
        </Button>
      </div>

      {/* Back to Login */}
      <Link 
        to="/login" 
        className="absolute top-4 left-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground z-10"
      >
        <BackArrow className="w-4 h-4" />
        {language === 'ar' ? 'العودة لتسجيل الدخول' : 'Back to Login'}
      </Link>

      {/* Register Card */}
      <Card className="w-full max-w-md z-10 border-border/50 shadow-xl fade-in">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-20 h-20 rounded-full overflow-hidden bg-white shadow-lg flex items-center justify-center">
            <img src="/logo.png" alt="شركة التمكين" className="w-16 h-16 object-contain" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">
              {language === 'ar' ? 'إنشاء حساب جديد' : 'Create New Account'}
            </CardTitle>
            <CardDescription className="mt-2">
              {language === 'ar' ? 'سجّل كعميل للوصول إلى بوابة عملاء التمكين' : 'Register to access Tamkeen client portal'}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-sm bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الاسم الكامل' : 'Full Name'} *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                placeholder={language === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'اسم المستخدم' : 'Username'} *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  placeholder={language === 'ar' ? 'اسم المستخدم' : 'Username'}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'الهاتف' : 'Phone'} *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder={language === 'ar' ? 'رقم الهاتف' : 'Phone number'}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{language === 'ar' ? 'البريد الإلكتروني' : 'Email'} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'كلمة المرور' : 'Password'} *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm'} *</Label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder="••••••"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 btn-accent"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}
                </span>
              ) : (
                language === 'ar' ? 'إنشاء الحساب' : 'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-4 text-center text-sm text-muted-foreground">
            {language === 'ar' ? 'لديك حساب؟ ' : 'Already have an account? '}
            <Link to="/login" className="text-[hsl(var(--accent))] hover:underline">
              {language === 'ar' ? 'تسجيل الدخول' : 'Login'}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
